import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { invoke } from '../tauriBridge';
import { save, open } from '../tauriBridge';
import { writeTextFile, readTextFile } from '../tauriBridge';
import { useInstanceStore } from './instanceStore';

/** Get current active instance ID, fallback to "default" */
function iid(): string {
  return useInstanceStore.getState().activeInstanceId ?? 'default';
}
import type {
  SimulationState,
  VesselConfig,
  RouteConfig,
  RouteInfo,
  ManualWeather,
  GribInfo,
  PolarInfo,
  PolarData,
  SignalKConfig,
  SignalKStatus,
  SignalKStatusResponse,
  Nmea0183Config,
  Nmea0183Status,
  Nmea0183StatusResponse,
  UdpBroadcastConfig,
  UdpBroadcastStatus,
  UdpBroadcastStatusResponse,
  TcpServerConfig,
  TcpServerStatus,
  TcpServerStatusResponse,
  TransportType,
  NmeaData,
  MapMode,
  Waypoint,
  MultiRouteConfig,
  FaultConfig,
  FaultSeverity,
  AisTargetConfig,
  AisTargetState,
  Scenario,
  ScenarioInfo,
  ScenarioLoadResponse,
} from '../types';

// Persisted map state
export interface MapState {
  center: [number, number];
  zoom: number;
  basemap: 'google-hybrid' | 'osm' | 'openseamap';
}

const defaultMapState: MapState = {
  center: [-36.85, 174.76], // Auckland, NZ
  zoom: 13,
  basemap: 'google-hybrid',
};

interface SimulationStore {
  // Simulation State
  state: SimulationState | null;
  isLoading: boolean;
  error: string | null;

  // Route (persisted)
  route: RouteInfo | null;
  savedRouteConfig: RouteConfig | null;

  // Weather
  gribInfo: GribInfo | null;
  manualWeather: ManualWeather;
  useGrib: boolean;

  // Polars
  polarData: PolarData | null;

  // Signal-K
  signalkStatus: SignalKStatus;
  signalkError: string | null;
  signalkMessagesSent: number;

  // NMEA0183 TCP
  nmea0183Status: Nmea0183Status;
  nmea0183Error: string | null;
  nmea0183MessagesSent: number;

  // NMEA0183 UDP Broadcast
  udpBroadcastStatus: UdpBroadcastStatus;
  udpBroadcastError: string | null;
  udpBroadcastMessagesSent: number;

  // NMEA0183 TCP Server
  tcpServerStatus: TcpServerStatus;
  tcpServerError: string | null;
  tcpServerMessagesSent: number;
  tcpServerClientsConnected: number;

  // Active Transport (mutual exclusion)
  activeTransport: TransportType;

  // Faults
  faultCatalog: FaultConfig[];
  activeFaultCount: number;

  // AIS Targets
  aisTargets: AisTargetState[];
  aisEditingTarget: Partial<AisTargetConfig> | null;

  // Repeat Route
  repeatRoute: boolean;

  // Map (persisted)
  mapMode: MapMode;
  mapState: MapState;

  // Actions
  setMapMode: (mode: MapMode) => void;
  setMapState: (state: Partial<MapState>) => void;

  // Simulation Actions
  startSimulation: () => Promise<void>;
  stopSimulation: () => Promise<void>;
  pauseSimulation: () => Promise<void>;
  resumeSimulation: () => Promise<void>;
  setSimulationSpeed: (multiplier: number) => Promise<void>;
  fetchSimulationState: () => Promise<void>;
  toggleRepeatRoute: () => Promise<void>;

  // Vessel Actions
  setVesselConfig: (config: VesselConfig) => Promise<void>;
  fetchVesselConfig: () => Promise<VesselConfig>;

  // Route Actions
  setRoute: (config: RouteConfig) => Promise<void>;
  setRouteWaypoints: (waypoints: Waypoint[]) => Promise<void>;
  clearRoute: () => void;
  restoreRoute: () => Promise<void>;
  saveRouteToFile: () => Promise<void>;
  loadRouteFromFile: () => Promise<void>;

  // Weather Actions
  loadGribFile: (filePath: string) => Promise<void>;
  setManualWeather: (weather: ManualWeather) => Promise<void>;
  setUseGrib: (useGrib: boolean) => void;

  // Polar Actions
  loadPolarFile: (filePath: string) => Promise<void>;
  clearPolar: () => void;

  // Signal-K Actions
  connectSignalK: (config: SignalKConfig) => Promise<void>;
  disconnectSignalK: () => Promise<void>;
  fetchSignalKStatus: () => Promise<void>;

  // NMEA0183 TCP Actions
  connectNmea0183: (config: Nmea0183Config) => Promise<void>;
  disconnectNmea0183: () => Promise<void>;
  fetchNmea0183Status: () => Promise<void>;

  // NMEA0183 UDP Broadcast Actions
  connectUdpBroadcast: (config: UdpBroadcastConfig) => Promise<void>;
  disconnectUdpBroadcast: () => Promise<void>;
  fetchUdpBroadcastStatus: () => Promise<void>;

  // NMEA0183 TCP Server Actions
  startTcpServer: (config: TcpServerConfig) => Promise<void>;
  stopTcpServer: () => Promise<void>;
  fetchTcpServerStatus: () => Promise<void>;

  // Transport Switching
  disconnectAllTransports: () => Promise<void>;
  setActiveTransport: (transport: TransportType) => void;
  fetchActiveTransportStatus: () => Promise<void>;

  // Fault Actions
  fetchFaultCatalog: () => Promise<void>;
  enableFault: (id: string) => Promise<void>;
  disableFault: (id: string) => Promise<void>;
  clearAllFaults: () => Promise<void>;
  setFaultSeverity: (id: string, severity: FaultSeverity) => Promise<void>;

  // AIS Actions
  addAisTarget: (config: AisTargetConfig) => Promise<void>;
  removeAisTarget: (mmsi: number) => Promise<void>;
  clearAisTargets: () => Promise<void>;
  fetchAisTargets: () => Promise<void>;
  setAisEditingTarget: (target: Partial<AisTargetConfig> | null) => void;

  // Data Actions
  fetchNmeaData: () => Promise<NmeaData>;

  // Scenario Actions
  scenarios: ScenarioInfo[];
  listScenarios: () => Promise<void>;
  loadScenarioFile: (filename: string) => Promise<void>;
  saveScenarioFile: (filename: string, name?: string) => Promise<void>;
  deleteScenario: (filename: string) => Promise<void>;
  saveScenario: () => Promise<Scenario>;
}

const defaultManualWeather: ManualWeather = {
  wind_speed: 12.0,
  wind_direction: 45.0,
  wind_variability: 0.1,
  gust_factor: 1.3,
  current_speed: 0.5,
  current_direction: 180.0,
  pressure: 1013.25,
  temperature: 20.0,
};

export const useSimulationStore = create<SimulationStore>()(
  persist(
    (set, get) => {
      // Per-store instance tracking (not module-scope, scoped to this closure)
      let routeSynced = false;
      let currentInstanceId: string | null = null;

      return ({
      // Initial State
      state: null,
      isLoading: false,
      error: null,
      route: null,
      savedRouteConfig: null,
      gribInfo: null,
      manualWeather: defaultManualWeather,
      useGrib: false,
      polarData: null,
      signalkStatus: 'disconnected',
      signalkError: null,
      signalkMessagesSent: 0,
      nmea0183Status: 'disconnected',
      nmea0183Error: null,
      nmea0183MessagesSent: 0,
      udpBroadcastStatus: 'disconnected',
      udpBroadcastError: null,
      udpBroadcastMessagesSent: 0,
      tcpServerStatus: 'stopped',
      tcpServerError: null,
      tcpServerMessagesSent: 0,
      tcpServerClientsConnected: 0,
      activeTransport: 'none',
      faultCatalog: [],
      activeFaultCount: 0,
      aisTargets: [],
      aisEditingTarget: null,
      repeatRoute: false,
      scenarios: [],
      mapMode: 'pan',
      mapState: defaultMapState,

      // Map Mode
      setMapMode: (mode) => set({ mapMode: mode }),

      // Map State (persisted)
      setMapState: (newState) =>
        set((state) => ({
          mapState: { ...state.mapState, ...newState },
        })),

  // Simulation Actions
  startSimulation: async () => {
    try {
      set({ isLoading: true, error: null });
      await invoke('start_simulation', { instanceId: iid() });
      await get().fetchSimulationState();
    } catch (err) {
      set({ error: String(err) });
    } finally {
      set({ isLoading: false });
    }
  },

  stopSimulation: async () => {
    try {
      set({ isLoading: true, error: null });
      await invoke('stop_simulation', { instanceId: iid() });
      await get().fetchSimulationState();
    } catch (err) {
      set({ error: String(err) });
    } finally {
      set({ isLoading: false });
    }
  },

  pauseSimulation: async () => {
    try {
      await invoke('pause_simulation', { instanceId: iid() });
      await get().fetchSimulationState();
    } catch (err) {
      set({ error: String(err) });
    }
  },

  resumeSimulation: async () => {
    try {
      await invoke('resume_simulation', { instanceId: iid() });
      await get().fetchSimulationState();
    } catch (err) {
      set({ error: String(err) });
    }
  },

  setSimulationSpeed: async (multiplier) => {
    try {
      await invoke('set_simulation_speed', { instanceId: iid(), multiplier });
    } catch (err) {
      set({ error: String(err) });
    }
  },

  fetchSimulationState: async () => {
    try {
      const instanceId = iid();

      // Detect instance switch — reset local state so new instance data loads cleanly
      if (currentInstanceId !== null && currentInstanceId !== instanceId) {
        routeSynced = false;
        set({ route: null, state: null });
      }
      currentInstanceId = instanceId;

      const state = await invoke<SimulationState>('get_simulation_state', { instanceId });
      const updates: Partial<SimulationStore> = {
        state,
        activeFaultCount: state.active_faults?.length ?? 0,
        aisTargets: state.ais_targets ?? [],
        repeatRoute: state.repeat_route ?? false,
      };

      // Sync route from server: fetch once if client has no route
      const localRoute = get().route;
      if (!localRoute && !routeSynced) {
        try {
          const serverRoute = await invoke<RouteInfo | null>('get_route', { instanceId: iid() });
          if (serverRoute) {
            updates.route = serverRoute;
          }
          routeSynced = true; // Only mark synced after successful fetch
        } catch {
          // Will retry next poll
        }
      } else if (state.route_state && !localRoute) {
        // Simulation running with a route but client doesn't have it
        try {
          const serverRoute = await invoke<RouteInfo | null>('get_route', { instanceId: iid() });
          if (serverRoute) {
            updates.route = serverRoute;
          }
        } catch {
          // Ignore
        }
      }

      set(updates);
    } catch (err) {
      set({ error: String(err) });
    }
  },

  toggleRepeatRoute: async () => {
    const enabled = !get().repeatRoute;
    try {
      await invoke('set_repeat_route', { instanceId: iid(), enabled });
      set({ repeatRoute: enabled });
    } catch (err) {
      set({ error: String(err) });
    }
  },

  // Vessel Actions
  setVesselConfig: async (config) => {
    try {
      await invoke('set_vessel_config', { instanceId: iid(), config });
    } catch (err) {
      set({ error: String(err) });
    }
  },

  fetchVesselConfig: async () => {
    const config = await invoke<VesselConfig>('get_vessel_config', { instanceId: iid() });
    return config;
  },

  // Route Actions
  setRoute: async (config) => {
    try {
      set({ isLoading: true, error: null });
      const route = await invoke<RouteInfo>('set_route', { instanceId: iid(), config });
      set({ route, savedRouteConfig: config });
    } catch (err) {
      set({ error: String(err) });
    } finally {
      set({ isLoading: false });
    }
  },

  setRouteWaypoints: async (waypoints) => {
    try {
      set({ isLoading: true, error: null });
      const config: MultiRouteConfig = { waypoints };
      const route = await invoke<RouteInfo>('set_route_waypoints', { instanceId: iid(), config });
      // Convert back to simple config for persistence (first/last waypoints)
      const simpleConfig: RouteConfig = {
        start_lat: waypoints[0]?.latitude ?? 0,
        start_lon: waypoints[0]?.longitude ?? 0,
        start_name: waypoints[0]?.name,
        end_lat: waypoints[waypoints.length - 1]?.latitude ?? 0,
        end_lon: waypoints[waypoints.length - 1]?.longitude ?? 0,
        end_name: waypoints[waypoints.length - 1]?.name,
      };
      set({ route, savedRouteConfig: simpleConfig });
    } catch (err) {
      set({ error: String(err) });
    } finally {
      set({ isLoading: false });
    }
  },

  clearRoute: () => {
    invoke('clear_route', { instanceId: iid() }).catch(() => {});
    routeSynced = false;
    set({ route: null, savedRouteConfig: null, repeatRoute: false });
  },

  // Restore route from persisted config (called on app start)
  restoreRoute: async () => {
    const { savedRouteConfig } = get();
    if (savedRouteConfig) {
      try {
        const route = await invoke<RouteInfo>('set_route', { instanceId: iid(), config: savedRouteConfig });
        set({ route });
      } catch (err) {
        console.error('Failed to restore route:', err);
      }
    }
  },

  saveRouteToFile: async () => {
    const { route } = get();
    if (!route || !route.waypoints?.length) return;
    try {
      const filePath = await save({
        filters: [{ name: 'Route', extensions: ['json'] }],
        defaultPath: 'route.json',
      });
      if (!filePath) return;
      const data = JSON.stringify({ waypoints: route.waypoints }, null, 2);
      await writeTextFile(filePath, data);
    } catch (err) {
      set({ error: String(err) });
    }
  },

  loadRouteFromFile: async () => {
    try {
      const filePath = await open({
        filters: [{ name: 'Route', extensions: ['json'] }],
        multiple: false,
      });
      if (!filePath) return;
      const text = await readTextFile(filePath as string);
      const data = JSON.parse(text);
      if (!data.waypoints?.length || data.waypoints.length < 2) {
        set({ error: 'Route file must contain at least 2 waypoints' });
        return;
      }
      await get().setRouteWaypoints(data.waypoints);
    } catch (err) {
      set({ error: String(err) });
    }
  },

  // Weather Actions
  loadGribFile: async (filePath) => {
    try {
      set({ isLoading: true, error: null });
      const info = await invoke<GribInfo>('load_grib_file', { instanceId: iid(), filePath });
      set({ gribInfo: info, useGrib: true });
    } catch (err) {
      set({ error: String(err) });
    } finally {
      set({ isLoading: false });
    }
  },

  setManualWeather: async (weather) => {
    try {
      await invoke('set_manual_weather', { instanceId: iid(), weather });
      set({ manualWeather: weather, useGrib: false });
    } catch (err) {
      set({ error: String(err) });
    }
  },

  setUseGrib: (useGrib) => set({ useGrib }),

  // Polar Actions
  loadPolarFile: async (filePath) => {
    try {
      set({ isLoading: true, error: null });
      await invoke<PolarInfo>('load_polar_file', { instanceId: iid(), filePath });
      const polarData = await invoke<PolarData>('get_polar_data', { instanceId: iid() });
      set({ polarData });
    } catch (err) {
      set({ error: String(err) });
    } finally {
      set({ isLoading: false });
    }
  },

  clearPolar: () => set({ polarData: null }),

  // Signal-K Actions
  connectSignalK: async (config) => {
    try {
      set({ signalkStatus: 'connecting', signalkError: null });
      await invoke('connect_signalk', { instanceId: iid(), config });
      set({ signalkStatus: 'connected' });
    } catch (err) {
      set({ signalkStatus: 'error', signalkError: String(err) });
    }
  },

  disconnectSignalK: async () => {
    try {
      await invoke('disconnect_signalk', { instanceId: iid() });
      set({ signalkStatus: 'disconnected', signalkError: null });
    } catch (err) {
      set({ signalkError: String(err) });
    }
  },

  fetchSignalKStatus: async () => {
    try {
      const response = await invoke<SignalKStatusResponse>('get_signalk_status', { instanceId: iid() });
      set({
        signalkStatus: response.status,
        signalkError: response.error,
        signalkMessagesSent: response.messages_sent,
      });
    } catch (err) {
      // Ignore errors during status fetch
    }
  },

  // NMEA0183 TCP Actions
  connectNmea0183: async (config) => {
    try {
      set({ nmea0183Status: 'connecting', nmea0183Error: null });
      await invoke('connect_nmea0183', { instanceId: iid(), config });
      set({ nmea0183Status: 'connected' });
    } catch (err) {
      set({ nmea0183Status: 'error', nmea0183Error: String(err) });
    }
  },

  disconnectNmea0183: async () => {
    try {
      await invoke('disconnect_nmea0183', { instanceId: iid() });
      set({ nmea0183Status: 'disconnected', nmea0183Error: null });
    } catch (err) {
      set({ nmea0183Error: String(err) });
    }
  },

  fetchNmea0183Status: async () => {
    try {
      const response = await invoke<Nmea0183StatusResponse>('get_nmea0183_status', { instanceId: iid() });
      set({
        nmea0183Status: response.status,
        nmea0183Error: response.error,
        nmea0183MessagesSent: response.messages_sent,
      });
    } catch (err) {
      // Ignore errors during status fetch
    }
  },

  // NMEA0183 UDP Broadcast Actions
  connectUdpBroadcast: async (config) => {
    try {
      set({ udpBroadcastError: null });
      await invoke('connect_udp_broadcast', { instanceId: iid(), config });
      set({ udpBroadcastStatus: 'connected' });
    } catch (err) {
      set({ udpBroadcastStatus: 'error', udpBroadcastError: String(err) });
    }
  },

  disconnectUdpBroadcast: async () => {
    try {
      await invoke('disconnect_udp_broadcast', { instanceId: iid() });
      set({ udpBroadcastStatus: 'disconnected', udpBroadcastError: null });
    } catch (err) {
      set({ udpBroadcastError: String(err) });
    }
  },

  fetchUdpBroadcastStatus: async () => {
    try {
      const response = await invoke<UdpBroadcastStatusResponse>('get_udp_broadcast_status', { instanceId: iid() });
      set({
        udpBroadcastStatus: response.status,
        udpBroadcastError: response.error,
        udpBroadcastMessagesSent: response.messages_sent,
      });
    } catch {
      // Ignore errors during status fetch
    }
  },

  // NMEA0183 TCP Server Actions
  startTcpServer: async (config) => {
    try {
      set({ tcpServerError: null });
      await invoke('start_tcp_server', { instanceId: iid(), config });
      set({ tcpServerStatus: 'listening' });
    } catch (err) {
      set({ tcpServerStatus: 'error', tcpServerError: String(err) });
    }
  },

  stopTcpServer: async () => {
    try {
      await invoke('stop_tcp_server', { instanceId: iid() });
      set({ tcpServerStatus: 'stopped', tcpServerError: null, tcpServerClientsConnected: 0 });
    } catch (err) {
      set({ tcpServerError: String(err) });
    }
  },

  fetchTcpServerStatus: async () => {
    try {
      const response = await invoke<TcpServerStatusResponse>('get_tcp_server_status', { instanceId: iid() });
      set({
        tcpServerStatus: response.status,
        tcpServerError: response.error,
        tcpServerMessagesSent: response.messages_sent,
        tcpServerClientsConnected: response.clients_connected,
      });
    } catch {
      // Ignore errors during status fetch
    }
  },

  // Transport Switching
  disconnectAllTransports: async () => {
    try { await invoke('disconnect_signalk', { instanceId: iid() }); } catch { /* ignore */ }
    try { await invoke('disconnect_nmea0183', { instanceId: iid() }); } catch { /* ignore */ }
    try { await invoke('disconnect_udp_broadcast', { instanceId: iid() }); } catch { /* ignore */ }
    try { await invoke('stop_tcp_server', { instanceId: iid() }); } catch { /* ignore */ }
    set({
      signalkStatus: 'disconnected', signalkError: null, signalkMessagesSent: 0,
      nmea0183Status: 'disconnected', nmea0183Error: null, nmea0183MessagesSent: 0,
      udpBroadcastStatus: 'disconnected', udpBroadcastError: null, udpBroadcastMessagesSent: 0,
      tcpServerStatus: 'stopped', tcpServerError: null, tcpServerMessagesSent: 0, tcpServerClientsConnected: 0,
    });
  },

  setActiveTransport: (transport) => set({ activeTransport: transport }),

  fetchActiveTransportStatus: async () => {
    const { activeTransport } = get();
    switch (activeTransport) {
      case 'signalk': await get().fetchSignalKStatus(); break;
      case 'nmea0183': await get().fetchNmea0183Status(); break;
      case 'udpBroadcast': await get().fetchUdpBroadcastStatus(); break;
      case 'tcpServer': await get().fetchTcpServerStatus(); break;
    }
  },

  // Fault Actions
  fetchFaultCatalog: async () => {
    try {
      const catalog = await invoke<FaultConfig[]>('get_fault_catalog', { instanceId: iid() });
      set({ faultCatalog: catalog });
    } catch (err) {
      set({ error: String(err) });
    }
  },

  enableFault: async (id) => {
    try {
      await invoke('enable_fault', { instanceId: iid(), faultId: id });
      await get().fetchFaultCatalog();
    } catch (err) {
      set({ error: String(err) });
    }
  },

  disableFault: async (id) => {
    try {
      await invoke('disable_fault', { instanceId: iid(), faultId: id });
      await get().fetchFaultCatalog();
    } catch (err) {
      set({ error: String(err) });
    }
  },

  clearAllFaults: async () => {
    try {
      await invoke('clear_all_faults', { instanceId: iid() });
      await get().fetchFaultCatalog();
    } catch (err) {
      set({ error: String(err) });
    }
  },

  setFaultSeverity: async (id, severity) => {
    try {
      await invoke('set_fault_severity', { instanceId: iid(), faultId: id, severity });
      await get().fetchFaultCatalog();
    } catch (err) {
      set({ error: String(err) });
    }
  },

  // AIS Actions
  addAisTarget: async (config) => {
    try {
      await invoke('add_ais_target', { instanceId: iid(), config });
      await get().fetchAisTargets();
      set({ aisEditingTarget: null });
    } catch (err) {
      set({ error: String(err) });
    }
  },

  removeAisTarget: async (mmsi) => {
    try {
      await invoke('remove_ais_target', { instanceId: iid(), mmsi });
      await get().fetchAisTargets();
    } catch (err) {
      set({ error: String(err) });
    }
  },

  clearAisTargets: async () => {
    try {
      await invoke('clear_ais_targets', { instanceId: iid() });
      set({ aisTargets: [] });
    } catch (err) {
      set({ error: String(err) });
    }
  },

  fetchAisTargets: async () => {
    try {
      const targets = await invoke<AisTargetState[]>('get_ais_targets', { instanceId: iid() });
      set({ aisTargets: targets });
    } catch (err) {
      // Ignore errors during polling
    }
  },

  setAisEditingTarget: (target) => set({ aisEditingTarget: target }),

  // Data Actions
  fetchNmeaData: async () => {
    return await invoke<NmeaData>('get_current_nmea_data', { instanceId: iid() });
  },

  // Scenario Actions
  listScenarios: async () => {
    try {
      const scenarios = await invoke<ScenarioInfo[]>('list_scenarios');
      set({ scenarios });
    } catch (err) {
      set({ error: String(err) });
    }
  },

  loadScenarioFile: async (filename: string) => {
    try {
      set({ isLoading: true, error: null });
      await invoke<ScenarioLoadResponse>('load_scenario_file', { instanceId: iid(), filename });
      // Refresh state after scenario load
      const state = await invoke<SimulationState>('get_simulation_state', { instanceId: iid() });
      set({ state, isLoading: false, error: null });
    } catch (err) {
      set({ error: String(err), isLoading: false });
    }
  },

  saveScenarioFile: async (filename: string, name?: string) => {
    try {
      await invoke<ScenarioInfo>('save_scenario_file', { instanceId: iid(), filename, name });
      // Refresh the list
      const scenarios = await invoke<ScenarioInfo[]>('list_scenarios');
      set({ scenarios });
    } catch (err) {
      set({ error: String(err) });
    }
  },

  deleteScenario: async (filename: string) => {
    try {
      await invoke('delete_scenario', { filename });
      const scenarios = await invoke<ScenarioInfo[]>('list_scenarios');
      set({ scenarios });
    } catch (err) {
      set({ error: String(err) });
    }
  },

  saveScenario: async () => {
    return await invoke<Scenario>('save_scenario', { instanceId: iid() });
  },
    });},
    {
      name: 'nmea2000-emulator-storage',
      partialize: (state) => ({
        // Only persist these fields
        mapState: state.mapState,
        savedRouteConfig: state.savedRouteConfig,
        manualWeather: state.manualWeather,
      }),
    }
  )
);
